/**
 * The Ledger is the engine's PRIMARY output (PLAN.md guiding principle #1). Every chart,
 * percentile band, and success probability is an aggregation of it. When a number looks
 * wrong you drill into one path's ledger and read it like a bank statement via
 * `explain(ledger, year)`.
 *
 * Storage is COLUMNAR — each accounting line is its own `Float64Array` — so aggregation
 * over N paths is fast elementwise math (no numpy needed) and the drill-down row-view is a
 * cheap struct of column reads. The `period` column exists from day one (PLAN.md: annual
 * steps first, but monthly can be added without migration — a period is `year*12 + month`
 * once we go monthly; for annual steps it equals the year).
 *
 * COLUMNS = a fixed core set of plan-level aggregates (always present) PLUS optional dynamic
 * columns the M2 Build agents add per ACCOUNT and per expense CATEGORY for full drill-down:
 *  - per-account balance:   `acct.<accountId>.balance`     (end-of-period balance)
 *  - per-account flow lines: `acct.<accountId>.contribution`, `.withdrawal`, `.growth`
 *  - per-category spend:    `cat.<category>`                (expense by ExpenseCategory)
 *  - per-tax-category:      `tax.<categoryKey>`             (TaxResult.byCategory drill-down)
 * Column names are generated with the helpers below so producers and `explain` stay in sync.
 * The Ledger is constructed with its full column set (`new Ledger(length, extraColumns)`),
 * which is fixed for the path; the core aggregates are always included.
 */

/**
 * The ORIGINAL (M0–M5) core columnar accounting lines — always present, and REQUIRED on a
 * `LedgerRow` so the money-conservation contract is total. Kept separate from the ENRICHMENT
 * columns below so adding enrichment columns does NOT force every existing `setRow` caller (or
 * test fixture) to supply them — that is what keeps the M2 exact tests green unchanged.
 */
export const BASE_LEDGER_COLUMNS = [
  /** Period index. Annual steps: the calendar year. Monthly (later): year*12 + month. */
  'period',
  /** Total portfolio balance at the START of the period (before flows/growth). */
  'startBalance',
  /** Income applied this period. */
  'income',
  /** Expenses applied this period (stored as a positive magnitude). */
  'expenses',
  /** Contributions routed into accounts this period. */
  'contributions',
  /** Withdrawals taken from accounts this period (positive magnitude). */
  'withdrawals',
  /** Investment growth credited this period (return * invested balance). */
  'growth',
  /** Taxes assessed this period (positive magnitude). */
  'taxes',
  /** Total portfolio balance at the END of the period. */
  'endBalance',
] as const;

/**
 * ENRICHMENT aggregate columns (append-only; always allocated, default 0). They are OPTIONAL on a
 * `LedgerRow` so a pre-enrichment producer / a hand-built fixture stays valid — `setRow` simply
 * leaves them at 0 — and the M2 exact tests read the SAME values for the original columns.
 */
export const ENRICHED_LEDGER_COLUMNS = [
  /** Dividends ACCRUED this period across all accounts (per-account `dividendYield` × start
   *  balance). Feeds `YearTaxInput.dividends`. 0 when no account carries a yield. */
  'dividends',
  /** Realised capital gains on SET-listed / `taxable_set` sleeves this period (proceeds − basis
   *  fraction). EXEMPT for Thai individuals, but tracked for drill-down. 0 by default. */
  'realizedGainsSet',
  /** Realised capital gains on the foreign (`taxable_foreign`) sleeve this period. Taxed via the
   *  remittance rule. 0 by default. */
  'realizedGainsForeign',
  /** Proceeds from foreign-sleeve withdrawals/realisations REMITTED to Thailand this period
   *  (Por.161/162). Feeds `YearTaxInput.foreignIncomeRemitted`. 0 by default. */
  'foreignRemitted',
  /** Unfunded shortfall this period: withdrawals that exceeded available balances after the
   *  portfolio was floored at 0 (positive magnitude). 0 when fully funded. */
  'shortfall',
  /** DEPLETED flag for this period: 1 if the portfolio could not fund the year's outflows and was
   *  floored at 0 (i.e. `shortfall > 0`), else 0. A path is considered FAILED from its first
   *  depleted year (drives `successProbability`). */
  'depleted',
] as const;

/** The full fixed core columnar accounting lines, always allocated (base + enrichment). The
 *  Ledger constructor always includes these; `columnNames()` lists them (then any extras). */
export const CORE_LEDGER_COLUMNS = [...BASE_LEDGER_COLUMNS, ...ENRICHED_LEDGER_COLUMNS] as const;

/**
 * @deprecated kept as a back-compat alias for `CORE_LEDGER_COLUMNS`. Use `CORE_LEDGER_COLUMNS`
 * (always present) or `ledger.columnNames()` (core + dynamic) instead.
 */
export const LEDGER_COLUMNS = CORE_LEDGER_COLUMNS;

/** An original (always-required) core column name. */
export type BaseLedgerColumn = (typeof BASE_LEDGER_COLUMNS)[number];

/** An enrichment core column name (optional on a `LedgerRow`). */
export type EnrichedLedgerColumn = (typeof ENRICHED_LEDGER_COLUMNS)[number];

/** A fixed core column name (base OR enrichment). */
export type CoreLedgerColumn = (typeof CORE_LEDGER_COLUMNS)[number];

/**
 * Any ledger column name: a core aggregate, or a dynamic per-account / per-category /
 * per-tax column produced via the namespacing helpers. Dynamic columns are open strings.
 */
export type LedgerColumn = CoreLedgerColumn | (string & {});

/**
 * A single period's accounting, as a plain object view over the columnar store. The ORIGINAL core
 * aggregate columns are always present (typed `number`); the ENRICHMENT core columns are OPTIONAL
 * (so a pre-enrichment / hand-built row need not supply them — `setRow` leaves them at 0); dynamic
 * per-account / per-category columns are accessed via the open index signature (`number |
 * undefined` under `noUncheckedIndexedAccess`).
 */
export type LedgerRow = Record<BaseLedgerColumn, number> &
  Partial<Record<EnrichedLedgerColumn, number>> &
  Record<string, number>;

// ---------------------------------------------------------------------------
// Dynamic column name helpers (keep producers + explain in sync)
// ---------------------------------------------------------------------------

/** Per-account column name, e.g. `accountColumn('rmf','balance')` -> `acct.rmf.balance`. */
export function accountColumn(
  accountId: string,
  line: 'balance' | 'contribution' | 'withdrawal' | 'growth',
): string {
  return `acct.${accountId}.${line}`;
}

/** Per-expense-category column name, e.g. `categoryColumn('housing')` -> `cat.housing`. */
export function categoryColumn(category: string): string {
  return `cat.${category}`;
}

/** Per-tax-category column name, e.g. `taxColumn('pit')` -> `tax.pit`. */
export function taxColumn(category: string): string {
  return `tax.${category}`;
}

/**
 * Columnar, Float64Array-backed per-period ledger. Length and the set of columns are fixed
 * at construction (one slot per period); `setRow`/`set` fill cells during simulation. The
 * core aggregate columns are always present; pass `extraColumns` for per-account /
 * per-category / per-tax drill-down columns (generate names via the helpers above).
 */
export class Ledger {
  readonly length: number;
  /** All column names in this ledger (core aggregates first, then the extras in order). */
  private readonly names: readonly string[];
  private readonly columns: Record<string, Float64Array>;

  constructor(length: number, extraColumns: readonly string[] = []) {
    if (!Number.isInteger(length) || length < 0) {
      throw new RangeError(`Ledger length must be a non-negative integer, got ${length}`);
    }
    this.length = length;
    // De-duplicate while preserving order; core columns always come first.
    const seen = new Set<string>();
    const names: string[] = [];
    for (const name of [...CORE_LEDGER_COLUMNS, ...extraColumns]) {
      if (!seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    }
    this.names = names;
    const columns: Record<string, Float64Array> = {};
    for (const name of names) {
      columns[name] = new Float64Array(length);
    }
    this.columns = columns;
  }

  /** The full ordered list of column names (core aggregates + any extras). */
  columnNames(): readonly string[] {
    return this.names;
  }

  /** Whether a column exists in this ledger. */
  hasColumn(name: string): boolean {
    return name in this.columns;
  }

  /** Direct access to a raw column (for fast cross-path aggregation in results.ts). */
  column(name: LedgerColumn): Float64Array {
    const col = this.columns[name];
    if (col === undefined) {
      throw new RangeError(`Ledger has no column "${name}"`);
    }
    return col;
  }

  /** Write a single cell. */
  set(i: number, name: LedgerColumn, value: number): void {
    this.assertIndex(i);
    this.column(name)[i] = value;
  }

  /** Read a single cell. */
  get(i: number, name: LedgerColumn): number {
    this.assertIndex(i);
    return this.column(name)[i] as number;
  }

  /**
   * Write a whole period's row at once. Only keys that are columns are written; missing
   * columns are left at their current value (0 by default), and extra keys are ignored so a
   * partial aggregate-only row stays valid as Build agents add dynamic columns.
   */
  setRow(i: number, row: LedgerRow): void {
    this.assertIndex(i);
    for (const name of this.names) {
      const v = row[name];
      if (v !== undefined) {
        // `name` came from `this.names`, so the column is guaranteed allocated.
        (this.columns[name] as Float64Array)[i] = v;
      }
    }
  }

  /** Materialise period `i` as a plain object (the row-view used by `explain`). */
  row(i: number): LedgerRow {
    this.assertIndex(i);
    const out: Record<string, number> = {};
    for (const name of this.names) {
      out[name] = (this.columns[name] as Float64Array)[i] as number;
    }
    return out as LedgerRow;
  }

  /** Find the ledger index whose `period` column equals `period`, or -1. */
  indexOfPeriod(period: number): number {
    const col = this.columns.period as Float64Array;
    for (let i = 0; i < this.length; i++) {
      if (col[i] === period) {
        return i;
      }
    }
    return -1;
  }

  private assertIndex(i: number): void {
    if (!Number.isInteger(i) || i < 0 || i >= this.length) {
      throw new RangeError(`Ledger index out of range: ${i} (length ${this.length})`);
    }
  }
}

/**
 * Pretty-print one period's full accounting as a bank-statement-style string. This is the
 * human face of the ledger-first principle: `explain(ledger, year)` reads top to bottom as
 * start -> +income -> -expenses -> +/- flows -> +growth -> -taxes -> end, then lists any
 * per-account / per-category / per-tax drill-down columns present.
 *
 * `period` is matched against the ledger's `period` column (so callers pass a calendar year,
 * not an array index).
 */
export function explain(ledger: Ledger, period: number): string {
  const i = ledger.indexOfPeriod(period);
  if (i === -1) {
    return `No ledger row for period ${period}.`;
  }
  const r = ledger.row(i);
  const money = (n: number): string =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const line = (label: string, value: number): string =>
    `  ${label.padEnd(18)} ${money(value).padStart(18)}`;

  const lines = [
    `Ledger — period ${num(r.period)}`,
    line('start balance', num(r.startBalance)),
    line('+ income', num(r.income)),
    line('+ dividends', num(r.dividends)),
    line('- expenses', -num(r.expenses)),
    line('+ contributions', num(r.contributions)),
    line('- withdrawals', -num(r.withdrawals)),
    line('+ growth', num(r.growth)),
    line('- taxes', -num(r.taxes)),
    `  ${'-'.repeat(37)}`,
    line('end balance', num(r.endBalance)),
  ];

  // ENRICHMENT realised-gain / remittance / depletion drill-down. Only printed when non-zero (or
  // depleted) so pre-enrichment ledgers render the SAME bank statement they did before (the M2
  // explain golden output is unchanged for a plan that accrues nothing).
  const realizedSet = num(r.realizedGainsSet);
  const realizedForeign = num(r.realizedGainsForeign);
  const foreignRemitted = num(r.foreignRemitted);
  const shortfall = num(r.shortfall);
  const depleted = num(r.depleted);
  const enrich: string[] = [];
  if (realizedSet !== 0) {
    enrich.push(line('realised CG (SET, exempt)', realizedSet));
  }
  if (realizedForeign !== 0) {
    enrich.push(line('realised CG (foreign)', realizedForeign));
  }
  if (foreignRemitted !== 0) {
    enrich.push(line('foreign remitted', foreignRemitted));
  }
  if (shortfall !== 0 || depleted !== 0) {
    enrich.push(line('shortfall', shortfall));
    enrich.push(`  ${'DEPLETED'.padEnd(18)} ${(depleted ? 'yes' : 'no').padStart(18)}`);
  }
  if (enrich.length > 0) {
    lines.push(`  ${'·'.repeat(37)}`, ...enrich);
  }

  // Drill-down: list any dynamic per-account / per-category / per-tax columns present.
  const extras = ledger.columnNames().filter((n) => !isCoreColumn(n));
  if (extras.length > 0) {
    lines.push(`  ${'·'.repeat(37)}`);
    for (const name of extras) {
      lines.push(line(name, num(r[name])));
    }
  }
  return lines.join('\n');
}

function num(v: number | undefined): number {
  return v ?? 0;
}

function isCoreColumn(name: string): name is CoreLedgerColumn {
  return (CORE_LEDGER_COLUMNS as readonly string[]).includes(name);
}
